import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import toast from 'react-hot-toast';

import { Card } from '../../../components/ui/Card.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ragApi } from '../../../api/rag.js';
import { ApiError } from '../../../api/client.js';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';
import { SOURCE_TYPES } from './ragConstants.js';

function scoreTone(score) {
  return score >= 0.8 ? 'success' : score >= 0.5 ? 'warning' : 'neutral';
}

function scoreColor(score) {
  return score >= 0.8 ? 'bg-success' : score >= 0.5 ? 'bg-warning' : 'bg-ink-faint';
}

export function QueryPanel() {
  const [q, setQ] = useState('');
  const [topK, setTopK] = useState('8');
  const [sourceType, setSourceType] = useState('');
  const [results, setResults] = useState(null);

  const mutation = useMutation({
    mutationFn: () => ragApi.query({
      query: q,
      topK: Number(topK) || 8,
      ...(sourceType ? { sourceType } : {})
    }),
    onSuccess: (data) => setResults(data),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    mutation.mutate();
  };

  const items = (results && results.items) || [];

  return (
    <Card>
      <header className="mb-4">
        <h2 className="text-base font-semibold text-ink">Prova la ricerca semantica</h2>
        <p className="text-xs text-ink-muted">Interroga l'indice come farebbe l'assistente AI durante una conversazione.</p>
      </header>

      <div>
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
          <Input
            className="w-80"
            label="Domanda"
            placeholder="Es. quale cibo per cucciolo di Maltese?"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Select label="Tipo" className="w-44" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
            <option value="">Tutti</option>
            {SOURCE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <Input className="w-24" label="Risultati" type="number" min="1" max="50" value={topK} onChange={(e) => setTopK(e.target.value)} />
          <Button type="submit" loading={mutation.isPending} disabled={q.trim().length < 2}>
            <Search className="h-4 w-4" /> Cerca
          </Button>
        </form>

        {results && (
          <div className="mt-5">
            {items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-surface px-4 py-8 text-center">
                <p className="text-sm font-medium text-ink">Nessun risultato sopra la soglia</p>
                <p className="mt-1 text-xs text-ink-muted">Riformula la domanda o carica documenti pertinenti.</p>
              </div>
            ) : (
              <>
                <p className="mb-3 text-xs text-ink-muted">{items.length} passaggi più pertinenti</p>
                <ol className="flex flex-col gap-2.5">
                  {items.map((row, idx) => (
                    <li key={row._id || idx} className="rounded-lg border border-border bg-surface-elevated p-4">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-sm font-medium text-ink">
                          <span className="flex h-5 w-5 items-center justify-center rounded bg-surface-subtle text-[11px] text-ink-muted">{idx + 1}</span>
                          {row.documentTitle || row.documentId || `Risultato ${idx + 1}`}
                        </span>
                        <Badge tone={scoreTone(row.score)}>{fmt.dec(row.score)}</Badge>
                      </div>
                      <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-surface-subtle">
                        <div className={`h-full rounded-full ${scoreColor(row.score)}`} style={{ width: `${Math.min(100, Math.max(4, row.score * 100))}%` }} />
                      </div>
                      <p className="whitespace-pre-wrap text-sm text-ink-muted">{row.content}</p>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
